import { postSavePlayer, postSavePlayerAddress, postSavePlayerBio, postSavePlayerWithBio } from '@/api/endpoints';
import { postSavePlayerPhone } from '@/api/endpoints/postSavePlayerPhone';
import { SavePlayerAddressPayload, SavePlayerBioPayload, SavePlayerPayload, SavePlayerPhonePayload, SavePlayerWithBioPayload, SavePlayerWithBioResponse } from '@/api/models';
import { PlayerDB } from '@/db/repositories';
import { createNewPlayer } from '@/utils/createNewPlayer';

export const savePlayer = async (payload: SavePlayerWithBioPayload): Promise<SavePlayerWithBioResponse> => {
  try {
    const response = await postSavePlayerWithBio(payload);
    const newPlayer = createNewPlayer({ response: response.data, payload });
    await PlayerDB.add(newPlayer); // add new player to local indexed db
    return response.data;
  } catch (error) {
    console.error(`Error while trying to save a player in the PlayerHelper: ${error}`);
    throw error;
  }
};

interface IEditPlayerProps {
  playerId: number;
  savePlayerPayload: SavePlayerPayload;
  savePlayerBioPayload: SavePlayerBioPayload;
}

export const editPlayer = async ({ playerId, savePlayerPayload, savePlayerBioPayload }: IEditPlayerProps) => {
  const player = await PlayerDB.get(playerId);
  if (!player) throw new Error(`PlayerHelper editPlayer missing IndexedDB player, playerId: ${playerId}`);

  try {
    // Need to run two separate API POST in sequence on account of the legacy system
    // In the legacy system editing a player happens in many UI sections and involves multiple APIs
    const tempPlayerPayload = await postSavePlayer(savePlayerPayload);
    await postSavePlayerBio(savePlayerBioPayload);

    // Update local player info
    player.firstName = savePlayerPayload.fn;
    player.lastName = savePlayerPayload.ln;
    player.fullName = savePlayerPayload.full;
    player.defaultTourId = savePlayerPayload.defaulttourid;
    player.defaultTourName = savePlayerPayload.defaulttourname;
    player.currentPlayerRankingsPlayerId = savePlayerPayload.currentPlayerRankingsPlayerId;
    player.playerBioDto = {
      ...player.playerBioDto, // keep unchanged values
      caddy: savePlayerBioPayload.caddy,
      ...(savePlayerBioPayload.dob && { dob: savePlayerBioPayload.dob }), // only change dob if it has a value
      email: savePlayerBioPayload.email,
      fullName: savePlayerBioPayload.fullName,
      manager: savePlayerBioPayload.manager,
      nationality: savePlayerBioPayload.nationality,
      notes: savePlayerBioPayload.notes,
      playerCollegeLookupId: savePlayerBioPayload.playerCollegeLookupId,
      playerCollegeLookupName: savePlayerBioPayload.playerCollegeLookupName,
      playerInternalStatusLookupId: savePlayerBioPayload.playerInternalStatusLookupId,
      playerInternalStatusLookupName: savePlayerBioPayload.playerInternalStatusLookupName,
      playerRegionLookupId: savePlayerBioPayload.playerRegionLookupId,
      playerRegionLookupName: savePlayerBioPayload.playerRegionLookupName,
      playerStatusLookupId: savePlayerBioPayload.playerStatusLookupId,
      playerStatusLookupName: savePlayerBioPayload.playerStatusLookupName,
      playerSupportLookupId: savePlayerBioPayload.playerSupportLookupId,
      playerSupportLookupName: savePlayerBioPayload.playerSupportLookupName,
      playerYearLookupId: savePlayerBioPayload.playerYearLookupId,
      playerYearLookupName: savePlayerBioPayload.playerYearLookupName,
    };
    player.currentPlayerRanking = (tempPlayerPayload.data as any).playerResults.data.currentPlayerRanking;
    await PlayerDB.update(player, playerId);
    return player;
  } catch (error) {
    console.error(`PlayerHelper editPlayer player update error: ${error}`);
    throw error;
  }
};

export const savePlayerPhone = async (payload: SavePlayerPhonePayload) => {
  try {
    const player = await PlayerDB.get(payload.playerId);
    if (!player) throw new Error(`PlayerHelper editPlayer missing IndexedDB player, playerId: ${payload.playerId}`);

    const response = await postSavePlayerPhone(payload);
    const playerPhoneResponse = response.data.playerPhoneResults.data;

    // update local state
    let updatedPhones = [...(player.phonesResponse ?? [])];

    // check if there already is a default one and flip the state of the other phones
    const hasAnotherDefault = updatedPhones.some((phone) => phone.isDefault);
    if (payload.isDefault && hasAnotherDefault) {
      updatedPhones = updatedPhones.map((phone) => ({ ...phone, isDefault: false }));
    }

    if (payload.phoneId === -1) {
      // creating a new phone
      updatedPhones.push({
        ...payload,
        phoneId: playerPhoneResponse.phoneId, // get new phoneId from backend
        createDate: new Date(),
        createUserGuid: '',
        modifyDate: new Date(),
        modifyUserGuid: '',
        modifyUserName: '',
        rowGuid: '',
        tstamp: '',
      });
    } else {
      // editing an existing phone
      updatedPhones = updatedPhones.map((phone) => {
        if (phone.phoneId === payload.phoneId) {
          return {
            ...phone,
            code: playerPhoneResponse.code,
            phoneNo: playerPhoneResponse.phoneNo,
            isDefault: playerPhoneResponse.isDefault,
          };
        }
        return phone;
      });
    }

    player.phonesResponse = updatedPhones; // update player phones

    await PlayerDB.update(player, payload.playerId);
    return player;
  } catch (error) {
    console.error(`Error when saving a player phone: ${error}`);
    throw error;
  }
};

export const savePlayerAddress = async (payload: SavePlayerAddressPayload) => {
  try {
    const player = await PlayerDB.get(payload.playerId);
    if (!player) throw new Error(`PlayerHelper editPlayer missing IndexedDB player, playerId: ${payload.playerId}`);

    const response = await postSavePlayerAddress(payload);
    const playerAddressResponse = response.data.playerAddressResults.data;

    // update local state
    let updatedAddresses = [...(player.addresses ?? [])];

    // check if there already is a default one and flip the state of the other addresses
    const hasAnotherDefault = updatedAddresses.some((address) => address.isDefault);
    if (payload.isDefault && hasAnotherDefault) {
      updatedAddresses = updatedAddresses.map((address) => ({ ...address, isDefault: false }));
    }

    if (payload.addressId === -1) {
      // creating a new address
      updatedAddresses.push({
        ...payload,
        addressId: playerAddressResponse.addressId, // get new addressId from backend
        objectId: playerAddressResponse.objectId, // get new objectId from backend
        address2: playerAddressResponse.address2 || '',
        address3: '', // not supported
        createDate: new Date(),
        createUserGuid: '',
        modifyDate: new Date(),
        modifyUserGuid: '',
        modifyUserName: '',
        rowGuid: '',
        tstamp: '',
      });
    } else {
      // editing an existing phone
      updatedAddresses = updatedAddresses.map((address) => {
        if (address.addressId === payload.addressId) {
          return {
            ...address,
            isDefault: playerAddressResponse.isDefault,
            code: playerAddressResponse.code,
            address1: playerAddressResponse.address1,
            address2: playerAddressResponse.address2,
            city: playerAddressResponse.city,
            state: playerAddressResponse.state,
            country: playerAddressResponse.country,
            postalCode: playerAddressResponse.postalCode,
          };
        }
        return address;
      });
    }

    if (!payload.isActive) {
      // filter inactive addresses
      updatedAddresses = updatedAddresses.filter((address) => address.addressId !== payload.addressId);
    }

    player.addresses = updatedAddresses; // update player addresses

    await PlayerDB.update(player, payload.playerId);
    return player;
  } catch (error) {
    console.error(`Error when saving a player phone: ${error}`);
    throw error;
  }
};
